import type { UserRole } from '../types'

export const FULL_ACCESS_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER']
export const PROJECT_OPERATOR_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'LEADER']
export const REPORT_ACCESS_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER']
export const USER_EDITOR_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN']
export const ALL_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'LEADER', 'TESTER', 'DEVELOPER', 'CLIENT']
export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  LEADER: 'Leader',
  TESTER: 'Tester',
  DEVELOPER: 'Team',
  CLIENT: 'Client',
}

export function hasFullAccess(role?: UserRole): boolean {
  return !!role && FULL_ACCESS_ROLES.includes(role)
}

export function isTesterRole(role?: UserRole): boolean {
  return role === 'TESTER'
}

export function canOperateProject(role?: UserRole): boolean {
  return !!role && PROJECT_OPERATOR_ROLES.includes(role)
}

export function canViewReports(role?: UserRole): boolean {
  return !!role && REPORT_ACCESS_ROLES.includes(role)
}

export function canCreateMainIssue(role?: UserRole): boolean {
  return canOperateProject(role) || isTesterRole(role)
}

export function canCreateSubIssue(
  role: UserRole | undefined,
): boolean {
  return canOperateProject(role) || role === 'DEVELOPER' || isTesterRole(role)
}

export function canEditIssue(
  role: UserRole | undefined,
  issue: { createdById: string; parentId?: string | null },
  userId?: string,
): boolean {
  if (!role || !userId) return false
  if (canOperateProject(role)) return true
  if (isTesterRole(role)) return issue.createdById === userId
  return role === 'DEVELOPER' && !!issue.parentId
}

export function canDeleteIssue(
  role: UserRole | undefined,
  issue: { createdById: string },
  userId?: string,
): boolean {
  if (!role || !userId) return false
  if (hasFullAccess(role)) return true
  if (role === 'CLIENT') return false
  return issue.createdById === userId
}

export function canMoveIssue(
  role: UserRole | undefined,
  issue: { createdById: string; assignees?: Array<{ userId: string }> },
  userId?: string,
): boolean {
  if (!role || !userId) return false
  if (canOperateProject(role) || isTesterRole(role)) return true
  if (role === 'CLIENT') return false
  return (issue.assignees || []).some((assignee) => assignee.userId === userId)
}

export function canViewUserDirectory(role?: UserRole): boolean {
  return hasFullAccess(role)
}

export function canEditUsers(role?: UserRole): boolean {
  return !!role && USER_EDITOR_ROLES.includes(role)
}

export function canManageSensitiveUserFields(role?: UserRole): boolean {
  return role === 'SUPER_ADMIN'
}

export function formatRoleLabel(role?: UserRole): string {
  if (!role) return 'Unknown'
  return ROLE_LABELS[role] || role.replace(/_/g, ' ')
}
